import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import LoginPage from "./pages/LoginPage";
import LandingPage from "./pages/LandingPage";
import Dashboard from "./pages/Dashboard";
import LojaVirtualPage from "./pages/LojaVirtualPage";
import CotacaoLandingPage from "./pages/cotacao/CotacaoLandingPage";
import CotacaoLoginPage from "./pages/cotacao/CotacaoLoginPage";
import CotacaoDashboard from "./pages/cotacao/CotacaoDashboard";
import CotacaoLojaFornecedorPage from "./pages/cotacao/CotacaoLojaFornecedorPage";
import VaiPedeLoginPage from "./pages/vaipede/VaiPedeLoginPage";
import VaiPedeBoasVindasPage from "./pages/vaipede/VaiPedeBoasVindasPage";
import VaiPedeDashboard from "./pages/vaipede/VaiPedeDashboard";
import VaiPedeCardapioPublicoPage from "./pages/vaipede/VaiPedeCardapioPublicoPage";
import EntregadorLoginPage from "./pages/vaipede/EntregadorLoginPage";
import EntregadorDashboardPage from "./pages/vaipede/EntregadorDashboardPage";
import VaiPedeGarcomLoginPage from "./pages/vaipede/VaiPedeGarcomLoginPage";
import VaiPedeGarcomDashboardPage from "./pages/vaipede/VaiPedeGarcomDashboardPage";
import { Toaster } from "./components/ui/sonner";
import UpgradeModal from "./components/UpgradeModal";
import { abrirUpgradeModal, carregarPlanoAtual, invalidarCachePlano } from "./lib/planos";

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

// Setup axios interceptor
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Normaliza erros do backend: quando o FastAPI/Pydantic devolve `detail` como array
// de objetos {type, loc, msg, input, ctx}, achatamos para uma string legível.
// Isso evita que componentes (toast.error etc) tentem renderizar um objeto e
// quebrem toda a árvore React com "Objects are not valid as a React child".
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    try {
      // HTTP 402 = feature bloqueada por plano → dispara modal global de upgrade
      if (error?.response?.status === 402) {
        const d = error.response.data?.detail;
        if (d && typeof d === 'object') {
          abrirUpgradeModal(d);
          // Mantém detail como string legível para quem renderiza erros
          error.response.data.detail = d.message || 'Recurso bloqueado pelo seu plano atual.';
          return Promise.reject(error);
        }
      }
      const data = error?.response?.data;
      if (data && data.detail !== undefined && data.detail !== null) {
        const d = data.detail;
        if (Array.isArray(d)) {
          // Pydantic validation errors → "campo: mensagem; campo2: mensagem2"
          const msgs = d.map((it) => {
            if (typeof it === 'string') return it;
            if (it && typeof it === 'object') {
              const loc = Array.isArray(it.loc) ? it.loc.filter((p) => p !== 'body').join('.') : '';
              const msg = it.msg || it.message || 'erro de validação';
              return loc ? `${loc}: ${msg}` : msg;
            }
            return String(it);
          });
          error.response.data.detail = msgs.join('; ') || 'Erro de validação';
        } else if (typeof d === 'object') {
          error.response.data.detail = d.msg || d.message || JSON.stringify(d);
        }
      }
    } catch (_) { /* noop */ }
    return Promise.reject(error);
  }
);

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.get(`${API}/auth/me`)
        .then((res) => {
          // Atualiza o user do localStorage com dados frescos (inclui super_admin atualizado)
          if (res && res.data) {
            try { localStorage.setItem('user', JSON.stringify(res.data)); } catch (e) { /* noop */ }
          }
          // Carrega plano vigente em background
          invalidarCachePlano();
          carregarPlanoAtual().catch(() => {});
          setIsAuthenticated(true);
          setLoading(false);
        })
        .catch(() => {
          localStorage.removeItem('token');
          setIsAuthenticated(false);
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, []);

  // Atualiza manifest/favicon/apple-touch-icon dinâmicos do PWA com a logo da loja
  // do admin logado — assim, ao instalar o app pelo Chrome do PC ou celular, o ícone
  // já vira a logo configurada (em vez do default DAL COTONE MS).
  useEffect(() => {
    if (!isAuthenticated) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/loja/config`);
        const path = data?.perfil_loja?.logo_path;
        const u = JSON.parse(localStorage.getItem("user") || "{}");
        const uid = u?.id;
        if (!path || !uid) return;
        const manifestUrl = `${API}/loja/manifest?user_id=${uid}`;
        let link = document.querySelector('link[rel="manifest"]');
        if (!link) { link = document.createElement("link"); link.rel = "manifest"; document.head.appendChild(link); }
        link.href = manifestUrl;
        const fn = String(path).split("/").pop();
        const logoFileUrl = `${API}/loja/logo-file/${fn}`;
        let apple = document.querySelector('link[rel="apple-touch-icon"]');
        if (!apple) { apple = document.createElement("link"); apple.rel = "apple-touch-icon"; document.head.appendChild(apple); }
        apple.href = logoFileUrl;
        let fav = document.querySelector('link[rel="icon"]');
        if (!fav) { fav = document.createElement("link"); fav.rel = "icon"; document.head.appendChild(fav); }
        fav.href = logoFileUrl;
      } catch (_) { /* noop */ }
    })();
  }, [isAuthenticated]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Carregando...</div>;
  }

  return (
    <div className="App">
      <Toaster position="top-center" />
      <BrowserRouter>
        <UpgradeModal />
        <Routes>
          <Route path="/loja" element={<LojaVirtualPage />} />
          {/* CD Sistema de Cotações — produto SaaS separado, cadastro isolado */}
          <Route path="/cotacoes" element={<CotacaoLandingPage />} />
          <Route path="/cotacoes/login" element={<CotacaoLoginPage />} />
          <Route path="/cotacoes/responder/:token" element={<CotacaoLojaFornecedorPage />} />
          <Route path="/cotacoes/dashboard/*" element={<CotacaoDashboard />} />
          {/* VAI PEDE — SaaS para restaurantes e lanchonetes */}
          <Route path="/vaipede" element={<VaiPedeLoginPage />} />
          <Route path="/vaipede/login" element={<VaiPedeLoginPage />} />
          <Route path="/vaipede/boas-vindas" element={<VaiPedeBoasVindasPage />} />
          <Route path="/vaipede/dashboard/*" element={<VaiPedeDashboard />} />
          <Route path="/vaipede/cardapio/:slug" element={<VaiPedeCardapioPublicoPage />} />
          <Route path="/vaipede/entregador/login" element={<EntregadorLoginPage />} />
          <Route path="/vaipede/entregador/painel" element={<EntregadorDashboardPage />} />
          <Route path="/vaipede/garcom/login" element={<VaiPedeGarcomLoginPage />} />
          <Route path="/vaipede/garcom" element={<VaiPedeGarcomDashboardPage />} />
          <Route
            path="/login"
            element={!isAuthenticated ? <LoginPage setIsAuthenticated={setIsAuthenticated} /> : <Navigate to="/" />}
          />
          <Route
            path="/planos-publico"
            element={!isAuthenticated ? <LandingPage setIsAuthenticated={setIsAuthenticated} /> : <Navigate to="/" />}
          />
          <Route
            path="/*"
            element={isAuthenticated ? <Dashboard /> : <LandingPage setIsAuthenticated={setIsAuthenticated} />}
          />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
